import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";

const ORG_FIELDS = [
  ["legal_name", "Legal name"], ["trading_name", "Trading name"], ["registration_number", "Registration number"],
  ["headquarters", "Headquarters"], ["website", "Website"], ["industry", "Industry"],
  ["sustainability_contact", "Sustainability contact"], ["epd_contact", "EPD contact"],
];
const ROLES = [
  ["epd_manager", "EPD Manager"], ["data_contributor", "Data Contributor"],
  ["reviewer", "Reviewer"], ["executive_viewer", "Executive Viewer"], ["org_admin", "Organisation Admin"],
];

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "org_admin" || user?.role === "super_admin";
  const [org, setOrg] = useState({});
  const [users, setUsers] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ role: "epd_manager" });
  const [pw, setPw] = useState({});

  const changePw = async () => {
    try { await api.post("/auth/change-password", pw); toast.success("Password updated"); setPw({}); }
    catch (e) { toast.error(apiError(e)); }
  };

  const loadUsers = () => { if (isAdmin) api.get("/users").then(({ data }) => setUsers(data)); };
  useEffect(() => {
    api.get("/organization").then(({ data }) => setOrg(data || {}));
    loadUsers();
  }, []);

  const saveOrg = async () => {
    try { await api.put("/organization", org); toast.success("Organisation updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendInvite = async () => {
    try {
      await api.post("/users/invite", invite);
      toast.success("Invitation email sent");
      setInviteOpen(false); setInvite({ role: "epd_manager" }); loadUsers();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (u) => {
    try {
      await api.post(`/users/${u.id}/${u.status === "deactivated" ? "reactivate" : "deactivate"}`);
      loadUsers();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Organisation profile and user management." />
      {user?.subscription && (
        <div className="mb-5 rounded-lg border bg-white px-4 py-3 text-sm" data-testid="subscription-summary">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
            <div><span className="text-slate-500">Plan:</span>{" "}
              <b>{user.subscription.plan === "MONTHLY" ? "Enterprise Monthly" : "Enterprise Annual"}</b></div>
            <div><span className="text-slate-500">Named users:</span>{" "}
              <b data-testid="cs-seats">{user.subscription.active_named_users} / {user.subscription.contracted_named_users}</b></div>
            <div><span className="text-slate-500">Subscription value:</span>{" "}
              <b data-testid="cs-value">₦{Number(user.subscription.subtotal || 0).toLocaleString()}</b></div>
            <div><span className="text-slate-500">Payment:</span>{" "}
              <span data-testid="cs-payment" className={`text-xs rounded-full border px-2.5 py-0.5 font-medium ${user.subscription.payment_status === "PAID" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                {user.subscription.payment_status === "PENDING" ? "Payment pending"
                  : user.subscription.payment_status === "PAID" ? "Paid"
                  : user.subscription.payment_status === "UNPAID" ? "Unpaid"
                  : user.subscription.payment_status}
              </span></div>
            <div><span className="text-slate-500">Subscription:</span>{" "}
              <span data-testid="cs-subscription" className="text-xs">{user.subscription.subscription_status}</span></div>
          </div>
        </div>
      )}
      {user?.must_change_password && (
        <div data-testid="must-change-pw" className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          You are using a bootstrap password. Please set a new password below before sharing this account.
        </div>
      )}
      <Tabs defaultValue="org">
        <TabsList>
          <TabsTrigger value="org" data-testid="tab-org">Organisation</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
        </TabsList>

        <TabsContent value="org" className="mt-5">
          <div className="bg-white border rounded-lg p-6 max-w-3xl">
            <div className="grid grid-cols-2 gap-4">
              {ORG_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <Input data-testid={`settings-org-${k}`} value={org[k] || ""} disabled={!isAdmin}
                    onChange={(e) => setOrg((o) => ({ ...o, [k]: e.target.value }))} className="mt-1" />
                </div>
              ))}
            </div>
            {isAdmin && <div className="mt-5"><Button data-testid="save-org-btn" onClick={saveOrg}>Save changes</Button></div>}
          </div>
          <div className="bg-white border rounded-lg p-6 max-w-3xl mt-5">
            <h3 className="font-medium mb-3">Change password</h3>
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div className="col-span-2"><Label className="text-xs">Current password</Label><Input data-testid="cp-current" type="password" value={pw.current_password || ""} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} className="mt-1" /></div>
              <div className="col-span-2"><Label className="text-xs">New password (min 8)</Label><Input data-testid="cp-new" type="password" value={pw.new_password || ""} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} className="mt-1" /></div>
            </div>
            <div className="mt-4"><Button data-testid="cp-submit" onClick={changePw} disabled={!pw.current_password || !pw.new_password}>Update password</Button></div>
          </div>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users" className="mt-5">
            <div className="flex justify-end mb-4">
              <Button data-testid="invite-user-btn" onClick={() => setInviteOpen(true)}><UserPlus className="h-4 w-4 mr-1.5" /> Invite user</Button>
            </div>
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full text-sm epn-table">
                <thead className="bg-slate-50 border-b"><tr className="text-left">
                  <th className="px-5 py-2.5">Name</th><th className="px-5 py-2.5">Email</th>
                  <th className="px-5 py-2.5">Role</th><th className="px-5 py-2.5">Status</th>
                  <th className="px-5 py-2.5">Last login</th><th className="px-5 py-2.5 text-right">Action</th>
                </tr></thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b last:border-0">
                      <td className="px-5 py-3 font-medium">{u.name}</td>
                      <td className="px-5 py-3 text-slate-600">{u.email}</td>
                      <td className="px-5 py-3 text-slate-600">{u.role_label}</td>
                      <td className="px-5 py-3"><span className="text-xs rounded border px-2 py-0.5 bg-slate-50">{u.status}</span></td>
                      <td className="px-5 py-3 text-xs text-slate-500">{(u.last_login || "").slice(0, 16).replace("T", " ") || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        {u.id !== user.id && (
                          <Button size="sm" variant="ghost" data-testid={`toggle-user-${u.id}`} onClick={() => toggle(u)}>
                            {u.status === "deactivated" ? "Reactivate" : "Deactivate"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invite user</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs">Full name</Label><Input data-testid="invite-user-name" value={invite.name || ""} onChange={(e) => setInvite((i) => ({ ...i, name: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs">Email</Label><Input data-testid="invite-user-email" type="email" value={invite.email || ""} onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))} className="mt-1" /></div>
            <div>
              <Label className="text-xs">Role</Label>
              <Select value={invite.role} onValueChange={(v) => setInvite((i) => ({ ...i, role: v }))}>
                <SelectTrigger data-testid="invite-user-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button data-testid="send-invite-btn" onClick={sendInvite} disabled={!invite.email || !invite.name}>Send invitation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
